import { z } from "zod";

/**
 * Organizasyon OPERASYON durumu (CLOS-195 `[054]`).
 *
 * ### ⚠ Üç eksen, üç ayrı state
 *
 * Kart: *"organizasyon operasyon durumu, abonelik/lisans/ödeme durumundan AYRI state ve
 * kaynakla tutulur"* ve *"kapatma tek başına aboneliği iptal etmez."* Ürünün üç bağımsız
 * ekseni var ve hiçbiri diğerini ima etmiyor:
 *
 * 1. **Operasyon** (burası): organizasyon çalışıyor mu?
 * 2. **Lisans/abonelik** (D06): hakları var mı?
 * 3. **Hesap** (`[014]`): kullanıcının kendi hesabı açık mı?
 *
 * Tek alanda birleştirilseydi *"aboneliği bitti"* ile *"yönetici kapattı"* ayırt edilemez
 * olurdu ve ikisinin gerektirdiği adım tamamen farklı.
 *
 * ### Kapatma bir SİLME DEĞİL
 *
 * Kart hard-delete'i açıkça yasaklıyor: kimlik, üyelik/davet geçmişi, roller,
 * departmanlar, sözleşmeler, audit ve fatura kanıtları yerinde kalıyor. Kapatma yalnız
 * YENİ YAZMAYI durduruyor.
 */
export const ORGANIZATION_STATUSES = ["active", "closed"] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export const ORGANIZATION_STATUS_NAMES: Record<OrganizationStatus, string> = {
  active: "Aktif",
  closed: "Kapalı",
};

/**
 * İzinli geçişler.
 *
 * ⚠ Aynı duruma "geçiş" bir geçiş değil: idempotentlik komut katmanının işi (kart:
 * *"kapatma ve yeniden etkinleştirme atomik/İDEMPOTENTTİR; paralel istekler çelişkili son
 * durum üretmez"*).
 */
export const ORGANIZATION_TRANSITIONS: Record<OrganizationStatus, readonly OrganizationStatus[]> = {
  active: ["closed"],
  closed: ["active"],
};

/**
 * Sürümlü politika (kartın AÇIK KARARI: *"durum geçiş yetkileri, gerekçe kategorileri,
 * yeniden doğrulama ve hukuki/güvenlik bekleme süreleri sürümlü politikadır"*).
 *
 * Değerler `config/organization.json`'da; burada yalnız o dosyanın ŞEKLİ. Doğrulanıyor:
 * elle yazılan bir JSON'daki hata sessizce kapalı bir yeniden doğrulama demek olurdu ve
 * arızayı ilk kullanıcı keşfederdi (`security.json` ile aynı desen).
 */
export const organizationLifecycleConfigSchema = z.object({
  closureReasons: z.array(z.object({ code: z.string().min(1), label: z.string().min(1) })).min(1),
  /** Kapatma parola ile yeniden doğrulama ister mi. */
  requireReauthentication: z.boolean(),
  /**
   * Kapatmadan sonra yeniden etkinleştirmeden ÖNCE beklenmesi gereken süre.
   *
   * `0` = bekleme yok. Kartın *"hukuki/güvenlik bekleme süreleri"* parametresi; sıfır
   * bırakmak da bir karar ve o kararın okunur bir yeri olmalı.
   */
  reactivationWaitHours: z.number().int().nonnegative(),
});
export type OrganizationLifecycleConfig = z.infer<typeof organizationLifecycleConfigSchema>;
export type ClosureReason = OrganizationLifecycleConfig["closureReasons"][number];

/**
 * Kapatma girdisi.
 *
 * ⚠ İKİ kanıt: gerekçe VE açık onay bayrağı; politika isterse üçüncüsü parola. Yalnız
 * bayrak isteseydik çalınmış bir oturum bütün organizasyonu kapatabilirdi (`[049]`
 * hesap kapatmadaki aynı gerekçe).
 */
export const closeOrganizationSchema = z.object({
  reason: z
    .string({ error: "Kapatma gerekçesi seçilmelidir" })
    .min(1, "Kapatma gerekçesi seçilmelidir"),
  note: z.string().max(500, "Not en fazla 500 karakter olabilir").optional(),
  confirm: z.literal(true, { error: "Organizasyonu kapatmayı onaylamalısınız" }),
  /** Politika `requireReauthentication` ise zorunlu; değilse gönderilmez. */
  currentPassword: z.string().optional(),
});
export type CloseOrganizationInput = z.infer<typeof closeOrganizationSchema>;

/**
 * Yeniden etkinleştirme girdisi.
 *
 * Gerekçe İSTENMİYOR: kapatma bir kayıp üretir ve nedeni sorulur; açmak kaybı geri
 * almaz, yalnız durdurulmuş işi sürdürür. Onay yine de isteniyor — kapalı bir
 * organizasyonu yanlışlıkla açmak da bir karardır.
 */
export const reactivateOrganizationSchema = z.object({
  confirm: z.literal(true, { error: "Yeniden etkinleştirmeyi onaylamalısınız" }),
});
export type ReactivateOrganizationInput = z.infer<typeof reactivateOrganizationSchema>;

/**
 * Kapatmanın ETKİ ÖZETİ (kart: *"işlem öncesi ... güvenli etki özetiyle gösterilir"*).
 *
 * ⚠ Sayılar, isimler değil ve KARARDAN ÖNCE. Kapatma yanıtında dönseydi yönetici neyi
 * durdurduğunu ancak durdurduktan sonra öğrenirdi.
 *
 * Bugün sayılabilen dört sınıf var. Sözleşme, workflow görevi, paylaşım ilişkisi ve
 * entegrasyon alanları YOK: o domainler kurulmadı (EP06+) ve hep sıfır dönen alanlar
 * koymak, hiçbir kodun beslemediği bir söz vermek olurdu (ADR-0014).
 */
export const organizationClosurePreviewSchema = z.object({
  activeMemberships: z.number().int().nonnegative(),
  pendingInvitations: z.number().int().nonnegative(),
  activeDepartments: z.number().int().nonnegative(),
  customRoles: z.number().int().nonnegative(),
  /**
   * Kapatmanın YAPMADIKLARI — kullanıcıya söyleniyor.
   *
   * Kart: *"kapatma tek başına aboneliği iptal etmez, ödeme iadesi oluşturmaz veya saklama
   * politikasını değiştirmez."* Söylenmeseydi yönetici organizasyonu kapatıp faturanın da
   * durduğunu sanır, bir sonraki tahsilatta şaşırırdı.
   */
  preserved: z.array(z.string()),
});
export type OrganizationClosurePreview = z.infer<typeof organizationClosurePreviewSchema>;

/**
 * Kapatmanın kullanıcıya söylenen SONUÇLARI.
 *
 * `@closer/shared`'da çünkü sunucu bunu ön izlemede döndürüyor ve arayüz onayı almadan
 * önce göstermek zorunda; iki kopya olsaydı sessizce ayrışırdı (`[038]`'in sonuç
 * listesiyle aynı gerekçe).
 */
export const ORGANIZATION_CLOSURE_CONSEQUENCES: readonly string[] = [
  "Organizasyonda yeni kayıt oluşturulamaz ve mevcut kayıtlar değiştirilemez.",
  "Yeni kullanıcı davet edilemez ve yeni erişim verilemez.",
  "Geçmiş kayıtlar, denetim izi ve imzalı belgeler KORUNUR; hiçbir veri silinmez.",
  "Yetkili kullanıcılar geçmişi salt okunur görmeye devam eder.",
];

/**
 * Kapatmanın DEĞİŞTİRMEDİKLERİ (`preserved` alanının kanonik içeriği).
 *
 * Sunucu bu listeyi ön izlemede döndürüyor; arayüz kendi cümlesini yazmıyor.
 */
export const ORGANIZATION_CLOSURE_PRESERVED: readonly string[] = [
  "Abonelik kendiliğinden iptal edilmez; ticari yaşam döngüsü ayrı bir işlemdir.",
  "Ödeme iadesi oluşturulmaz.",
  "Saklama politikası değişmez.",
];
